import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PublicShell } from "../components/PublicShell";
import { ShieldCheck, AlertTriangle, Car, FileSignature, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/verify/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Unable to verify this document"))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="brand-divider max-w-xs mx-auto mb-4">
            <span className="dot" />
            <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Document verification</span>
            <span className="dot" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl">
            <span className="chrome-text">Verified by</span> <span className="gold-text">Wakzy.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-8">
        {loading && <div className="surface p-12 rounded-md text-center text-slate-400">Verifying…</div>}

        {!loading && error && (
          <div className="surface p-10 rounded-md border-l-4 border-[#E63946]" data-testid="verify-error">
            <AlertTriangle className="w-10 h-10 text-[#E63946] mb-3" />
            <h2 className="font-heading text-2xl mb-2 text-white">Verification failed</h2>
            <p className="text-slate-300">{error}</p>
            <p className="text-xs text-slate-500 mt-4">If you believe this is an error, contact us at (770) 647-2563 or info@wakzyauto.com.</p>
            <Link to="/" className="inline-block mt-6 text-[#D4AF37] hover:underline text-sm">← Back to wakzyautokiosk.com</Link>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5">
            <div className="gradient-border" style={{ borderRadius: "16px" }}>
              <div className="gradient-border-inner p-8 flex items-center gap-5" style={{ borderRadius: "15px" }} data-testid="verify-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(16,185,129,0.15)", border: "2px solid #10b981" }}>
                  <ShieldCheck className="w-9 h-9 text-emerald-400" />
                </div>
                <div>
                  <div className="label-eyebrow" style={{ color: "#10b981" }}>Document authentic</div>
                  <h2 className="font-heading text-2xl chrome-text inline-block">This Bill of Sale is verified</h2>
                  <p className="text-slate-300 text-sm mt-1">
                    Issued by <strong className="text-white">Wakzy Auto Sales &amp; Services LLC</strong> on <strong className="text-white">{data.issued_at}</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard icon={Car} label="Vehicle" value={data.vehicle || "—"} sub={data.vin ? `VIN ${data.vin}` : ""} accent="kpi-chrome" />
              <InfoCard icon={FileSignature} label="Buyer" value={data.buyer_name_masked || "—"} sub={data.is_signed ? "Signed by buyer" : "Awaiting buyer signature"} accent="kpi-navy" />
              <InfoCard icon={ShieldCheck} label="Deal ID" value={data.deal_id_short} sub={`Status: ${data.status}`} accent="kpi-gold" mono />
              <InfoCard icon={MapPin} label="Issuing dealer" value={data.dealer} sub={data.dealer_address} accent="kpi-emerald" />
            </div>

            <div className="surface p-5 rounded-md text-xs text-slate-500 leading-relaxed">
              This verification confirms that a Bill of Sale with the deal ID <span className="text-slate-300 font-mono">{data.deal_id_short}</span> exists on file at Wakzy Auto Sales &amp; Services LLC and that the vehicle, dealer, and issuance date displayed here match the issued document.
              For pricing, full buyer details, or a certified copy, contact the dealer directly at <a href="tel:+17706472563" className="text-[#D4AF37] hover:underline">(770) 647-2563</a> or <a href="mailto:info@wakzyauto.com" className="text-[#D4AF37] hover:underline">info@wakzyauto.com</a>.
            </div>

            <Link to="/" className="text-[#D4AF37] hover:underline text-sm inline-block">← Back to wakzyautokiosk.com</Link>
          </div>
        )}
      </section>
    </PublicShell>
  );
}

function InfoCard({ icon: Icon, label, value, sub, accent, mono }) {
  return (
    <div className={`surface kpi-accent ${accent} p-5 rounded-md`}>
      <Icon className="w-5 h-5 mb-3" style={{ color: "var(--accent-color)" }} />
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`font-heading text-lg text-white ${mono ? "font-mono" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}
